import React from "react";
import { withApollo } from "@apollo/client/react/hoc";
import { get } from "lodash";
import moment from "moment";
import { detectFieldChanges, getMetaValue } from "utils/typeUtils";
import type { ApprovalItem } from "types/models";

import ComparisonCard from "./ComparisonCard";
import ApprovalCard from "./ApprovalCard";

// getMetaValue expects { name?; value? }[]; ApprovalItem.metadataItems is
// (ApprovalMetadataItem | null)[] which is structurally compatible.
type MetadataItems = Parameters<typeof getMetaValue>[0];

interface Props {
  approval?: ApprovalItem;
}

const formatDate = (value: unknown) => {
  if (!value) return "N/A";

  // getMetaValue returns unknown; moment accepts MomentInput at runtime
  const parsed = moment(
    value as moment.MomentInput,
    "ddd MMM DD HH:mm:ss z YYYY"
  );
  if (parsed.isValid()) {
    return parsed.format("MM/DD/YYYY");
  }
  const isoDate = moment(value as moment.MomentInput);
  if (isoDate.isValid()) {
    return isoDate.format("MM/DD/YYYY");
  }
  return value;
};

const BusinessMemberProfileUpdateApproval = ({ approval }: Props) => {
  // lodash get widens; cast once to the shape getMetaValue expects
  const metadataItems = get(approval, "metadataItems", []) as MetadataItems;
  const companyName = get(approval, "correlationName", "");

  // getMetaValue returns unknown; rendered directly as a JSX child below
  const businessMemberId = getMetaValue(
    metadataItems,
    "businessMemberId"
  ) as React.ReactNode;

  // SSN fields
  const currentSsn = getMetaValue(metadataItems, "Ssn/oldValue");
  const newSsn = getMetaValue(metadataItems, "Ssn/newValue");
  // operands are unknown; flag is only used for truthiness (compile-time cast,
  // runtime value unchanged)
  const hasSsnChange = (currentSsn || newSsn) as boolean;

  // DOB fields - only show if dates are actually different (ignore time/timezone)
  const currentDobRaw = getMetaValue(metadataItems, "Dob/oldValue");
  const newDobRaw = getMetaValue(metadataItems, "Dob/newValue");
  const currentDob = formatDate(currentDobRaw);
  const newDob = formatDate(newDobRaw);
  // operands are unknown; flag is only used for truthiness (compile-time cast,
  // runtime value unchanged)
  const hasDobChange = (currentDobRaw &&
    newDobRaw &&
    !moment(currentDobRaw as moment.MomentInput).isSame(
      moment(newDobRaw as moment.MomentInput),
      "day"
    )) as boolean;

  // Build SSN changes
  const ssnChanges = hasSsnChange
    ? detectFieldChanges({ ssn: currentSsn }, { ssn: newSsn }, { ssn: "SSN" })
    : [];

  // Build DOB changes
  const dobChanges = hasDobChange
    ? detectFieldChanges(
        { dob: currentDob },
        { dob: newDob },
        { dob: "Date of Birth" }
      )
    : [];

  // Build updated fields list based on actual changes
  const updatedFields = [hasSsnChange && "SSN", hasDobChange && "Date of Birth"]
    .filter(Boolean)
    .join(", ");

  return (
    <>
      <ApprovalCard title="Member Information">
        <div className="row">
          <div className="col-md-6">
            <p className="account-info-label">Business Member ID</p>
            <p className="account-info-value">{businessMemberId || "N/A"}</p>
          </div>
          <div className="col-md-6">
            <p className="account-info-label">Company</p>
            <p className="account-info-value">{companyName || "N/A"}</p>
          </div>
        </div>
        {updatedFields && (
          <div className="row" style={{ marginTop: 16 }}>
            <div className="col-md-12">
              <p className="account-info-label">Fields Being Updated</p>
              <p className="account-info-value">{updatedFields}</p>
            </div>
          </div>
        )}
      </ApprovalCard>

      {/* currentSsn/newSsn come from getMetaValue (`unknown`); `|| "N/A"` yields
          `{} | "N/A"`. currentDob/newDob come from formatDate (`string | unknown`).
          All are rendered as ComparisonCard children (React nodes); the casts
          preserve the exact runtime values the original JS passed. */}
      {hasSsnChange && (
        <ComparisonCard
          title="SSN Comparison"
          currentLabel="Current SSN"
          newChangeLabel="New SSN"
          current={(currentSsn || "N/A") as React.ReactNode}
          newChange={(newSsn || "N/A") as React.ReactNode}
          changes={ssnChanges}
        />
      )}

      {hasDobChange && (
        <ComparisonCard
          title="Date of Birth Comparison"
          currentLabel="Current Date of Birth"
          newChangeLabel="New Date of Birth"
          current={currentDob as React.ReactNode}
          newChange={newDob as React.ReactNode}
          changes={dobChanges}
        />
      )}
    </>
  );
};

export default withApollo<Props>(BusinessMemberProfileUpdateApproval);
